import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Snackbar, Switch, Typography,
} from '@mui/material';
import SupportAgentOutlined from '@mui/icons-material/SupportAgentOutlined';
import BusinessCenterOutlined from '@mui/icons-material/BusinessCenterOutlined';
import ForumOutlined from '@mui/icons-material/ForumOutlined';
import { cardStyle, cardsContainerStyle, configureButtonStyle } from './integrationCardStyles';
import { CustomizationRichTextEditor } from '../chat/CustomizationRichTextEditor';
import {
  getBusinessActionSuccessSettings, resetBusinessActionSuccessSetting, saveBusinessActionSuccessSetting,
} from '../../services/businessActionSuccessSettingsService';
import {
  sanitizeBusinessActionSuccessHtml, sanitizeBusinessActionSuccessEditorHtml,
  successMessageLimitError, successMessageVisibleText,
} from '../../utils/businessActionSuccessHtml';

const actionPresentation = [
  {
    key: 'create_technical_support_tickets',
    title: 'Technical Support',
    Icon: SupportAgentOutlined,
    description: 'Configure successful technical support ticket confirmations.',
  },
  {
    key: 'lead_opportunity_management',
    title: 'Sales & Lead Management',
    Icon: BusinessCenterOutlined,
    description: 'Configure successful sales and lead creation confirmations.',
  },
  {
    key: 'customer_service',
    title: 'Customer Service',
    Icon: ForumOutlined,
    description: 'Configure successful customer service case confirmations.',
  },
];

function requestError(error, operation) {
  const detail = error.response?.data?.message || error.response?.data?.reason;
  return `Could not ${operation}. ${typeof detail === 'string' ? detail : 'Please try again; check your access if the problem persists.'}`;
}

function BusinessActionSettingsModal({ agentId, action, onClose, onSaved }) {
  const [displayReferenceNumber, setDisplayReferenceNumber] = useState(action.setting.displayReferenceNumber);
  const [html, setHtml] = useState(() => sanitizeBusinessActionSuccessHtml(action.setting.additionalSuccessMessageHtml));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);
  const safeHtml = sanitizeBusinessActionSuccessHtml(html);
  const limitError = successMessageLimitError(safeHtml);

  const persist = async (reset) => {
    if (saving || (!reset && limitError)) return;
    setSaving(true);
    setError('');
    try {
      const descriptor = reset
        ? await resetBusinessActionSuccessSetting(agentId, action.businessActionKey)
        : await saveBusinessActionSuccessSetting(agentId, action.businessActionKey, {
          displayReferenceNumber, additionalSuccessMessageHtml: safeHtml,
        });
      if (!active.current) return;
      const returnedHtml = descriptor.setting.additionalSuccessMessageHtml || '';
      setDisplayReferenceNumber(descriptor.setting.displayReferenceNumber);
      setHtml(sanitizeBusinessActionSuccessHtml(returnedHtml));
      onSaved(descriptor, reset
        ? 'Reset to server defaults.'
        : returnedHtml !== safeHtml
          ? 'Saved. The server adjusted the message for safety or formatting; review the updated preview.'
          : 'Business Action settings saved.');
    } catch (failure) {
      if (active.current) setError(requestError(failure, reset ? 'reset settings' : 'save settings'));
    } finally {
      if (active.current) setSaving(false);
    }
  };

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="business-action-settings-title">
      <DialogTitle id="business-action-settings-title">{action.businessActionLabel}</DialogTitle>
      <DialogContent dividers>
        <FormControlLabel
          control={<Switch checked={displayReferenceNumber} disabled={saving} onChange={(event) => setDisplayReferenceNumber(event.target.checked)} />}
          label="Display Reference Number in Creation Confirmation"
        />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Controls the reference number in the initial creation confirmation only. Later status requests are unaffected.
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Additional success message</Typography>
        <Box sx={{ pointerEvents: saving ? 'none' : 'auto' }} aria-busy={saving}>
          <CustomizationRichTextEditor
            value={html}
            onChange={(event) => setHtml(event.target.value)}
            ariaLabel="Additional success message"
            placeholder="Optional message after successful creation"
            enableSourceEditor={false}
            enableStyles={false}
            enableStrikeThrough={false}
            sanitizeHtml={sanitizeBusinessActionSuccessEditorHtml}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          {successMessageVisibleText(safeHtml).length}/4000 visible characters; {safeHtml.length}/16000 HTML characters.
          Basic formatting and http/https links only. The server validates the final message.
        </Typography>
        {limitError && <Alert severity="error" sx={{ mt: 1 }}>{limitError}</Alert>}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Additional message preview</Typography>
        <Box
          role="region"
          aria-label="Additional message preview"
          sx={{ p: 1, border: '1px solid', borderColor: 'divider', minHeight: 40 }}
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => persist(true)} disabled={saving}>Reset to Defaults</Button>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={() => persist(false)} disabled={saving || Boolean(limitError)} variant="contained">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// The parent keys this section by agent, and the modal by action. Old requests may finish,
// but cannot mutate a newly selected agent/action or resurrect a closed draft.
export default function BusinessActionSettings({ agentId }) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(Boolean(agentId));
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [reload, setReload] = useState(0);
  const [notice, setNotice] = useState('');
  useEffect(() => {
    let active = true;
    setSelected(null);
    setActions([]);
    setError('');
    if (!agentId) {
      setLoading(false);
      return () => { active = false; };
    }
    setLoading(true);
    getBusinessActionSuccessSettings(agentId).then((data) => {
      if (active) setActions(data);
    }).catch((failure) => {
      if (active) setError(requestError(failure, 'load Business Action settings'));
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [agentId, reload]);

  return (
    <Box component="section" aria-label="Business Actions" sx={{ p: { xs: 0, sm: 2 } }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Business Actions</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Customize how your agent confirms successful Business Actions. These settings do not enable or disable tools or actions.
      </Typography>
      {loading && <CircularProgress size={24} aria-label="Loading Business Action settings" />}
      {error && <Alert severity="error" action={<Button onClick={() => setReload((value) => value + 1)}>Retry</Button>}>{error}</Alert>}
      {!agentId && <Typography>Select an agent to configure Business Action settings.</Typography>}
      <div style={cardsContainerStyle}>
        {actionPresentation.map(({ key, title, Icon, description }) => {
          const action = actions.find((item) => item.businessActionKey === key);
          if (!action) return null;
          return (
            <Box
              component="article"
              aria-label={title}
              key={key}
              style={{ ...cardStyle, width: 360, padding: undefined, margin: undefined }}
              sx={{ maxWidth: { xs: '100%', sm: 'calc(100% - 32px)' }, p: { xs: 2, sm: 4 }, m: { xs: '16px 0', sm: 2 } }}
            >
              <Typography component="h2" sx={{ fontWeight: 600, fontSize: 20, mb: '12px', minHeight: 60, display: 'flex', alignItems: 'center', textAlign: 'center' }}>{title}</Typography>
              <Icon sx={{ width: 64, height: 64, mb: '16px' }} />
              <Typography sx={{ fontSize: 15, color: '#555', textAlign: 'center', mb: '16px', minHeight: 48 }}>
                {description}
              </Typography>
              <Box sx={{ width: '100%', borderTop: 1, borderBottom: 1, borderColor: 'divider', py: 0.5, mb: 2 }}>
                {[
                  { label: 'ID in creation confirmation', active: action.setting.displayReferenceNumber, on: 'ON', off: 'OFF' },
                  { label: 'Custom message', active: Boolean(successMessageVisibleText(sanitizeBusinessActionSuccessHtml(action.setting.additionalSuccessMessageHtml))), on: 'Configured', off: 'None' },
                ].map((status) => (
                  <Box key={status.label} sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 100px', alignItems: 'center', gap: 1, py: 1 }}>
                    <Typography variant="body2" sx={{ textAlign: 'left' }}>{status.label}:</Typography>
                    <Chip
                      role="status"
                      aria-label={`${status.label}: ${status.active ? status.on : status.off}`}
                      label={status.active ? status.on : status.off}
                      color={status.active ? 'primary' : 'default'}
                      sx={{
                        height: 32,
                        width: 100,
                        fontWeight: 600,
                        bgcolor: status.active ? 'primary.main' : 'grey.200',
                        color: status.active ? 'common.white' : 'grey.900',
                        '& .MuiChip-label': { px: 1.5, py: 0.5 },
                      }}
                    />
                  </Box>
                ))}
              </Box>
              <button
                type="button"
                style={configureButtonStyle}
                onClick={() => setSelected(action)}
                aria-label={`Configure ${title}`}
              >Configure</button>
            </Box>
          );
        })}
      </div>
      {selected && (
        <BusinessActionSettingsModal
          key={selected.businessActionKey}
          agentId={agentId}
          action={selected}
          onClose={() => setSelected(null)}
          onSaved={(descriptor, message) => {
            setActions((current) => current.map((action) => action.businessActionKey === descriptor.businessActionKey ? descriptor : action));
            setNotice(message);
          }}
        />
      )}
      <Snackbar open={Boolean(notice)} autoHideDuration={6000} onClose={() => setNotice('')}>
        <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert>
      </Snackbar>
    </Box>
  );
}
